import type { Catalog, Song, SeasonKey } from "../types";

export interface Suggestion {
  song: Song;
  score: number;
  reasons: string[];
}

/**
 * Rank catalog songs for a given liturgical moment + season.
 * Priority: matches this moment > matches the season > already used by us > in repertoire.
 */
export function suggestSongs(
  catalog: Catalog,
  moment: string,
  season: SeasonKey
): Suggestion[] {
  const out: Suggestion[] = [];
  for (const song of catalog.songs) {
    let score = 0;
    const reasons: string[] = [];

    if (song.moments.includes(moment)) {
      score += 6;
      reasons.push("déjà chanté à ce moment");
    }
    if (song.seasons.includes(season)) {
      score += 4;
      reasons.push(`temps « ${season} »`);
    }
    if (song.usedCount > 0) {
      score += Math.min(song.usedCount, 3);
      reasons.push(`utilisé ${song.usedCount}×`);
    }
    if (song.sources.includes("notion")) {
      score += 1;
      reasons.push("au répertoire");
    }
    // Liturgical rule: no "Alléluia" chants in Lent.
    if (season === "careme" && /all[ée]luia/i.test(song.title)) {
      score -= 100;
    }
    // Songs with no moment data at all are generic repertoire — keep them, low score.
    out.push({ song, score, reasons });
  }
  out.sort((a, b) => b.score - a.score || a.song.title.localeCompare(b.song.title, "fr"));
  return out;
}

export function defaultOrdinary(catalog: Catalog): string {
  return catalog.ordinaries[0] || "Messe de Saint Jean";
}
